import { FC, Fragment, useContext, useEffect, useState } from 'react';
import { Hotel } from 'ts/hotel/interfaces/HotelResponse';
import { useSearchParams } from 'react-router-dom';
import { HotelFilterContext } from 'contexts/HotelFilterContext';
import { FilterModel, LocationValue } from 'ts/hotel/classes/FilterModel';
import { MapHotelContext } from 'contexts/MapHotelContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faMap, faN, faPrint, faTimes } from '@fortawesome/free-solid-svg-icons';
import ReportModalGoogle from './Google/ReportModalGoogle';
import HotelsGoogle from './Google/HotelsGoogle';
import HotelsOSM from './OSM/HotelsOSM';
import ReportModalOSM from './OSM/ReportModalOSM';
import { showWarning } from 'utils/utils';
import { FocusMarkerType, FocusedMarker, Position } from 'ts/hotel/interfaces/Generic';
import { findCenterOfPolygon, getKey } from 'utils/hotel';
import Dropdown from 'components/shared/Dropdown';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { faCircleDot } from '@fortawesome/free-regular-svg-icons';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { faLoader, faWaveTriangle } from '@fortawesome/sharp-duotone-solid-svg-icons';
import { getNeighborhood } from 'services/hotel';
import { HotelConfigContext } from 'contexts/HotelConfigContext';
import { mapSupplier } from 'constants/generic';

interface HotelsMapProps {
  hotels?: Hotel[];
  onFilterChange: (filter: FilterModel) => void;
  onLocationChange: (location: LocationValue) => void;
  searchToken: string;
}

const HotelsMap: FC<HotelsMapProps> = (props) => {
  const { filter, neighborhoodList, setNeighborhoodList } = useContext(HotelFilterContext);
  const { hotelIndex } = useContext(HotelConfigContext);
  const [centerMarker, setCenterMarker] = useState<Position>();
  const [additionalCenter, setAdditionalCenter] = useState<Position>();
  const [focusedMarker, setFocusedMarker] = useState<FocusedMarker>();
  const [center, setCenter] = useState<Position>();
  const [directions, setDirections] = useState<any>();
  const [from, setFrom] = useState<Position>();
  const [to, setTo] = useState<Position>();
  const [zoom, setZoom] = useState(11.8);
  const { centered, focusedHotel, setInitedMap, setResetLocation } = useContext(MapHotelContext);
  const [urlParams] = useSearchParams();
  const [openedReport, setOpenedReport] = useState(false);
  const [showPolygon, setShowPolygon] = useState(false);
  const [clearDate, setClearDate] = useState(new Date());

  useEffect(() => {
    // if (document.body.clientWidth > 1250) {
    setInitedMap(false);
    setTimeout(() => {
      setInitedMap(true);
    }, 100);
    // }
  }, [window.location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (filter?.location.value && filter?.location.value.lat !== 0) {
      setAdditionalCenter({
        ...filter.location.value,
        address: filter.location.value.name,
        htuKey: filter.location.isHotel ? 'true' : undefined,
      });
      setCenter(filter.location.value);
      setZoom(15);
    } else {
      setAdditionalCenter(undefined);
      setDefaultCenter();
    }
  }, [filter?.location.value]); //  eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setDefaultCenter();
    setShowPolygon(true);
    setFrom(undefined);
    setTo(undefined);
    setAdditionalCenter(undefined);
  }, [urlParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (centered) {
      props.onLocationChange({
        lat: centered.lat,
        lng: centered.lng,
        name: centered.name || '',
        isHotel: true,
      });
      setZoom(15);
    }
  }, [centered]); // eslint-disable-line

  useEffect(() => {
    if (filter) {
      props.onFilterChange(filter);
    }
  }, [neighborhoodList.length]); // eslint-disable-line

  useEffect(() => {
    if (focusedHotel) {
      setFocusedMarker({
        ...focusedHotel,
        open: true,
        name: focusedHotel.name || '',
        type: focusedHotel.type,
      });
      setAdditionalCenter({
        lat: focusedHotel.lat,
        lng: focusedHotel.lng,
      });
      setZoom(15);
    }
  }, [focusedHotel]); // eslint-disable-line react-hooks/exhaustive-deps

  const setDefaultCenter = async () => {
    const newCenter = {
      lat: parseFloat(urlParams.get('lat')!),
      lng: parseFloat(urlParams.get('lon')!),
    };

    setCenterMarker(newCenter);
    setCenter(newCenter);
    setAdditionalCenter(newCenter);
    setZoom(11.8);
  };

  const onClickMarker = (type: FocusMarkerType, position?: Position) => {
    if (position) {
      setFocusedMarker({
        ...position,
        open: true,
        name: '',
        type,
      });
      setCenter(position);
    }
  };

  const onClickClear = () => {
    setDirections(undefined);
    setFrom(undefined);
    setTo(undefined);
    setFocusedMarker(undefined);
    setAdditionalCenter(undefined);
    sessionStorage.removeItem(getKey('routeInstrHotels', hotelIndex));

    setDefaultCenter();
    setClearDate(new Date());
    setResetLocation?.(new Date());
  };

  const onClickDirections = () => {
    if (directions && directions.walking && directions.driving) {
      setOpenedReport(true);
    } else {
      showWarning('No route found');
    }
  };

  const MapProps = {
    center,
    zoom,
    centerMarker,
    hotels: props.hotels?.filter((hotel) => hotel.visible),
    onClickMarker,
    setDirections,
    setFrom,
    setTo,
    to,
    from,
    focusedMarker,
    setFocusedMarker,
    showPolygon,
    additionalCenter,
    centered,
    directions,
    clearDate,
  };

  const onClickNeighborhood = async (code: string, index: number) => {
    if (code === '-1') {
      setNeighborhoodList(neighborhoodList.map((n) => ({ ...n, active: false })));
      return;
    }

    if (code === '0') {
      setNeighborhoodList(neighborhoodList.map((n) => ({ ...n, active: false })));
      onClickClear();
      return;
    }

    if (neighborhoodList[index].active) {
      setNeighborhoodList((prev) =>
        prev.map((n, i) => ({ ...n, active: i === index ? false : n.active }))
      );
      return;
    }

    setNeighborhoodList(
      neighborhoodList.map((n, i) => ({ ...n, active: i === index ? true : n.active }))
    );

    const response = await getNeighborhood({ code, searchToken: props.searchToken });

    if (response) {
      setNeighborhoodList((prev) =>
        prev.map((item) => {
          if (item.code === code) {
            const choosenPolygon = response.result?.searchByECity.geoPolygons[0].listOfGeoPolygon;
            if (choosenPolygon) {
              const newCenter = findCenterOfPolygon(
                choosenPolygon.map((item) => ({
                  lat: parseFloat(item.latitude as any),
                  lng: parseFloat(item.longitude as any),
                }))
              );
              setAdditionalCenter(newCenter);
              setZoom(14);
            }

            return {
              ...item,
              geoPolygon: response.result?.searchByECity.geoPolygons.map((item) =>
                item.listOfGeoPolygon.map((item) => ({
                  lat: item.latitude,
                  lng: item.longitude,
                }))
              ) || [[]],
            };
          }

          return item;
        })
      );
    }
  };

  return (
    <Fragment>
      <div className="map-custom">
        <div
          className="map-custom-item"
          title="Default center"
          onClick={() => {
            setDefaultCenter();
            setAdditionalCenter(undefined);
            setResetLocation?.(new Date());
          }}
        >
          <FontAwesomeIcon icon={faCircleDot} color="darkviolet" />
        </div>
        {filter ? (
          <Tooltip content="Display exactly" position="top" noPlague>
            <div
              className="map-custom-item"
              style={{
                backgroundColor: filter.displayExactly ? 'rgba(255, 165, 0, 0.3)' : 'transparent',
              }}
              onClick={() => {
                props.onFilterChange({
                  ...filter,
                  displayExactly: !filter.displayExactly,
                });
              }}
            >
              <FontAwesomeIcon
                icon={faWaveTriangle as any}
                style={
                  {
                    '--fa-secondary-color': filter.displayExactly ? 'orange' : '#333',
                    '--fa-secondary-opacity': 1,
                  } as React.CSSProperties
                }
              />
            </div>
            <div></div>
          </Tooltip>
        ) : (
          <div className="map-custom-item">
            <FontAwesomeIcon icon={faWaveTriangle as any} style={{ cursor: 'not-allowed' }} />
          </div>
        )}
        <div
          className="map-custom-item"
          title="Show/Hide city boundaries"
          onClick={() => setShowPolygon(!showPolygon)}
        >
          <FontAwesomeIcon icon={faMap} />
        </div>
        <div className="map-custom-item" title="Clear" onClick={onClickClear}>
          <FontAwesomeIcon icon={faTimes} />
        </div>
        <div className="map-custom-item" title="Report" onClick={onClickDirections}>
          <FontAwesomeIcon icon={faPrint} />
        </div>

        <Dropdown
          byClick={true}
          opener={
            <Tooltip content="Neibourhood" position="top" noPlague>
              <div
                className="map-custom-item"
                style={{
                  backgroundColor: neighborhoodList?.some((n) => !!n.active)
                    ? 'rgba(0, 34, 255, 0.3)'
                    : 'transparent',
                  cursor: !neighborhoodList.length ? 'not-allowed' : 'pointer',
                  opacity: !neighborhoodList.length ? 0.5 : 1,
                }}
              >
                <FontAwesomeIcon
                  icon={faN}
                  style={
                    {
                      '--fa-secondary-color': neighborhoodList?.some((n) => !!n.active)
                        ? '#0022ff'
                        : '#333',
                      '--fa-secondary-opacity': 1,
                    } as React.CSSProperties
                  }
                />
              </div>
              <div></div>
            </Tooltip>
          }
        >
          {neighborhoodList?.map((n, index) => (
            <div
              className="c-dropdown-row"
              key={index}
              onClick={() => onClickNeighborhood(n.code, index)}
            >
              <span className="position-relative">
                <FontAwesomeIcon
                  icon={faCheck}
                  color="var(--color-success)"
                  className="mr-5"
                  style={{
                    opacity: n.active && n.geoPolygon?.length && n.geoPolygon?.[0]?.length ? 1 : 0,
                    position: 'absolute',
                  }}
                />
                <FontAwesomeIcon
                  icon={faTimes}
                  color="var(--color-danger)"
                  className="mr-5"
                  style={{
                    opacity: n.active && n.geoPolygon?.length && !n.geoPolygon?.[0]?.length ? 1 : 0,
                    position: 'absolute',
                  }}
                />
                <FontAwesomeIcon
                  icon={faLoader as any}
                  className="mr-5"
                  style={{
                    opacity: n.active && !n.geoPolygon?.length ? 1 : 0,
                    animation:
                      n.active && !n.geoPolygon?.length ? 'spin-load 1s linear infinite' : 'none',
                  }}
                />
              </span>

              {n.desc}
            </div>
          ))}
        </Dropdown>
      </div>
      {mapSupplier === 'google' && <HotelsGoogle {...MapProps} />}
      {mapSupplier === 'osm' && <HotelsOSM {...MapProps} />}
      {openedReport && mapSupplier === 'google' && (
        <ReportModalGoogle onClose={() => setOpenedReport(false)} directions={directions} />
      )}
      {openedReport && mapSupplier === 'osm' && (
        <ReportModalOSM onClose={() => setOpenedReport(false)} directions={directions} />
      )}
    </Fragment>
  );
};

export default withErrorBoundary(HotelsMap, 'HotelsMap');
